import { useState, useMemo, useRef, useEffect } from "react";

const COLORS = ["#4f46e5", "#f97316", "#10b981", "#ef4444", "#8b5cf6", "#06b6d4", "#f59e0b"];
const getColor = (depth) => COLORS[depth % COLORS.length];

// Expands search results that include ancestors into a flat deduplicated list
const expandWithAncestors = (items) => {
    const map = new Map();
    items.forEach(item => {
        // Add ancestors first (they won't have children info, just id/name/slug)
        (item.ancestors || []).forEach(a => {
            if (!map.has(a.id)) map.set(a.id, { id: a.id, name: a.name, slug: a.slug, parent: null, parent_name: null });
        });
        // Link ancestor chain parents
        if (item.ancestors?.length) {
            item.ancestors.forEach((a, i) => {
                const node = map.get(a.id);
                if (i > 0) node.parent = item.ancestors[i - 1].id;
            });
        }
        // Add the item itself
        if (!map.has(item.id)) map.set(item.id, { ...item });
    });
    return Array.from(map.values());
};

const buildFlatWithDepth = (items) => {
    const expanded = expandWithAncestors(items);
    const inPage = new Set(expanded.map(i => i.id));
    const map = {};
    expanded.forEach(i => { map[i.id] = { ...i, children: [] }; });
    expanded.forEach(i => {
        if (i.parent && inPage.has(i.parent)) map[i.parent].children.push(map[i.id]);
    });
    const roots = expanded.filter(i => !i.parent || !inPage.has(i.parent)).map(i => map[i.id]);

    const flatten = (nodes, depth) => {
        const result = [];
        nodes.forEach(node => {
            result.push({ ...node, depth });
            if (node.children?.length) result.push(...flatten(node.children, depth + 1));
        });
        return result;
    };

    return flatten(roots, 0);
};

const ProductCategoryTable = ({
    data = [],
    loading = false,
    loadingMore = false,
    hasMore = false,
    onEdit,
    onDelete,
    onLoadMore,
    onSearch,
    currentPage,
    nextPage,
    onPageChange
}) => {
    const [collapsed, setCollapsed] = useState({});
    const [localSearch, setLocalSearch] = useState("");
    const [activeAction, setActiveAction] = useState(null);
    const scrollRef = useRef(null);
    const debounceRef = useRef(null);
    const actionRef = useRef(null);

    const flat = useMemo(() => buildFlatWithDepth(data), [data]);

    const toggleCollapse = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

    const isVisible = (row) => {
        let current = row;
        while (current.parent) {
            if (collapsed[current.parent]) return false;
            const parent = flat.find(r => r.id === current.parent);
            if (!parent) break;
            current = parent;
        }
        return true;
    };

    const visibleRows = flat.filter(isVisible);

    const handleSearchChange = (val) => {
        setLocalSearch(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => onSearch?.(val), 500);
    };

    const handleClear = () => {
        setLocalSearch("");
        clearTimeout(debounceRef.current);
        onSearch?.("");
    };

    const handleScroll = () => { };

    // Close action menu
    useEffect(() => {
        const handler = (e) => {
            if (actionRef.current && !actionRef.current.contains(e.target)) setActiveAction(null);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => () => clearTimeout(debounceRef.current), []);

    // Check if a row is an ancestor-only node (not in original data)
    const isAncestorOnly = (row) => !data.find(d => d.id === row.id);

    return (
        <div>
            {/* Search */}
            <div className="d-flex mb-3">
                <div className="expense-searchcontainerstart d-flex">
                    <div className="search-container" style={{ position: "relative" }}>
                        <input
                            type="text"
                            style={{
                                width: "300px",
                                height: "45px",
                                backgroundColor: "#fff",
                                border: "1px solid #DBDBDB",
                                boxShadow: "0px 0px 10px rgba(187, 187, 187, 0.25)",
                                paddingRight: localSearch ? "60px" : "40px",
                            }}
                            value={localSearch}
                            onChange={e => handleSearchChange(e.target.value)}
                            placeholder="Search"
                        />
                        {localSearch && (
                            <button
                                onClick={handleClear}
                                style={{
                                    position: "absolute",
                                    right: "36px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#aaa",
                                    fontSize: "16px",
                                    lineHeight: 1,
                                    padding: "0 4px",
                                }}
                                title="Clear"
                            >×</button>
                        )}
                        <svg className="search-container-icon" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.5416 19.2497C15.3511 19.2497 19.2499 15.3508 19.2499 10.5413C19.2499 5.73186 15.3511 1.83301 10.5416 1.83301C5.73211 1.83301 1.83325 5.73186 1.83325 10.5413C1.83325 15.3508 5.73211 19.2497 10.5416 19.2497Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M20.1666 20.1663L18.3333 18.333" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div
                ref={scrollRef}
                // onScroll={() => {
                //     const el = scrollRef.current;
                //     if (!el || loadingMore || !hasMore) return;
                //     if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) onLoadMore?.();
                // }}
                style={{ border: "1px solid #EBEAF2", borderRadius: "12px", overflowY: "auto", maxHeight: "60vh" }}
            >
                <table className="table mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                    <thead style={{ background: "#f8f9fa", position: "sticky", top: 0, zIndex: 1 }}>
                        <tr>
                            <th style={thStyle}>Name</th>
                            <th style={thStyle}>Parent</th>
                            <th style={thStyle}>Slug</th>
                            <th style={{ ...thStyle, textAlign: "center", width: 80 }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={4} className="text-center py-5"><div className="spinner-border spinner-border-sm text-secondary" /></td></tr>
                        ) : visibleRows.length === 0 ? (
                            <tr><td colSpan={4} className="text-center py-5" style={{ color: "#aaa", fontSize: 14 }}>No data found</td></tr>
                        ) : (
                            <>
                                {visibleRows.map((row) => {
                                    const hasChildren = row.children?.length > 0;
                                    const isCollapsed = collapsed[row.id];
                                    const ancestorOnly = isAncestorOnly(row);
                                    const isActive = activeAction === row.id;

                                    return (
                                        <tr key={row.id} style={{ borderBottom: "1px solid #f5f5f5", background: ancestorOnly ? "#fafbff" : "white" }}>
                                            {/* Name */}
                                            <td style={{ ...tdStyle, paddingLeft: `${14 + row.depth * 20}px` }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    {hasChildren ? (
                                                        <button onClick={() => toggleCollapse(row.id)} style={toggleBtnStyle}>
                                                            <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                                                                {isCollapsed
                                                                    ? <path d="M3 2L7 5L3 8" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                    : <path d="M2 3L5 7L8 3" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                }
                                                            </svg>
                                                        </button>
                                                    ) : (
                                                        <span style={{ width: 16, display: "inline-block" }} />
                                                    )}
                                                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: getColor(row.depth), flexShrink: 0 }} />
                                                    <span style={{ fontSize: 13, fontWeight: row.depth === 0 ? 600 : 400, color: ancestorOnly ? "#888" : "#1a1a1a" }}>
                                                        {row.name}
                                                    </span>
                                                    {ancestorOnly && (
                                                        <span style={{ fontSize: 10, color: "#aaa", background: "#f0f0f0", borderRadius: 4, padding: "1px 5px" }}>path</span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Parent */}
                                            <td style={tdStyle}>
                                                {row.parent_name
                                                    ? <span style={{ fontSize: 12, color: "#666" }}>{row.parent_name}</span>
                                                    : <span style={{ color: "#ddd" }}>—</span>
                                                }
                                            </td>

                                            {/* Slug */}
                                            <td style={tdStyle}>
                                                <span style={{ fontSize: 11, color: "#999", fontFamily: "monospace", background: "#f5f5f5", padding: "2px 6px", borderRadius: 4 }}>
                                                    {row.slug || "—"}
                                                </span>
                                            </td>

                                            {/* Action */}
                                            <td style={{ ...tdStyle, textAlign: "center", position: "relative" }}>
                                                {!ancestorOnly && (
                                                    <div style={{ position: "relative", display: "inline-block" }} ref={isActive ? actionRef : null}>
                                                        <button
                                                            onClick={() => setActiveAction(isActive ? null : row.id)}
                                                            style={moreBtn}
                                                            title="Actions"
                                                        >
                                                            <span style={dotStyle} />
                                                            <span style={dotStyle} />
                                                            <span style={dotStyle} />
                                                        </button>
                                                        {isActive && (
                                                            <div style={dropdownStyle}>
                                                                {onEdit && (
                                                                    <button
                                                                        style={menuItemStyle}
                                                                        onMouseEnter={e => e.currentTarget.style.background = "#f5f7ff"}
                                                                        onMouseLeave={e => e.currentTarget.style.background = "white"}
                                                                        onClick={() => { onEdit(row); setActiveAction(null); }}
                                                                    >
                                                                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ marginRight: 7 }}>
                                                                            <path d="M13.8067 4.695C14.0667 4.435 14.0667 4.00167 13.8067 3.755L12.2467 2.195C12 1.935 11.5667 1.935 11.3067 2.195L10.08 3.415L12.58 5.915M2 11.5017V14.0017H4.5L11.8733 6.62167L9.37333 4.12167L2 11.5017Z" fill="#4f46e5" />
                                                                        </svg>
                                                                        Edit
                                                                    </button>
                                                                )}
                                                                {onDelete && (
                                                                    <button
                                                                        style={{ ...menuItemStyle, color: "#e53e3e" }}
                                                                        onMouseEnter={e => e.currentTarget.style.background = "#fff5f5"}
                                                                        onMouseLeave={e => e.currentTarget.style.background = "white"}
                                                                        onClick={() => { onDelete(row.id); setActiveAction(null); }}
                                                                    >
                                                                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ marginRight: 7 }}>
                                                                            <path d="M4.00016 12.6667C4.00016 13.4 4.60016 14 5.3335 14H10.6668C11.4002 14 12.0002 13.4 12.0002 12.6667V6C12.0002 5.26667 11.4002 4.66667 10.6668 4.66667H5.3335C4.60016 4.66667 4.00016 5.26667 4.00016 6V12.6667ZM12.0002 2.66667H10.3335L9.86016 2.19333C9.74016 2.07333 9.56683 2 9.3935 2H6.60683C6.4335 2 6.26016 2.07333 6.14016 2.19333L5.66683 2.66667H4.00016C3.6335 2.66667 3.3335 2.96667 3.3335 3.33333C3.3335 3.7 3.6335 4 4.00016 4H12.0002C12.3668 4 12.6668 3.7 12.6668 3.33333C12.6668 2.96667 12.3668 2.66667 12.0002 2.66667Z" fill="#e53e3e" />
                                                                        </svg>
                                                                        Delete
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {loadingMore && (
                                    <tr><td colSpan={4} className="text-center py-3">
                                        <div className="spinner-border spinner-border-sm text-secondary" />
                                        <span style={{ marginLeft: 8, fontSize: 12, color: "#888" }}>Loading more…</span>
                                    </td></tr>
                                )}
                                {!hasMore && visibleRows.length > 0 && (
                                    <tr><td colSpan={4} className="text-center py-2" style={{ fontSize: 11, color: "#ccc" }}>
                                        All {data.length} categories loaded
                                    </td></tr>
                                )}
                            </>
                        )}
                    </tbody>
                </table>
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px",
                    borderTop: "1px solid #eee",
                    background: "#fafafa"
                }}>
                    <div style={{
                        display: "flex",
                        gap: "6px",
                        padding: "8px 12px",
                        borderRadius: "20px",
                        background: "#f5f5f5"
                    }}>

                        {/* PREV */}
                        <button
                            disabled={currentPage === 1}
                            onClick={() => onPageChange(currentPage - 1)}
                            style={{
                                border: "none",
                                background: "transparent",
                                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                                padding: "4px 8px",
                                color: "#666"
                            }}
                        >
                            ‹
                        </button>

                        {/* CURRENT PAGE */}
                        <div style={{
                            minWidth: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "6px",
                            border: "2px solid orange",
                            color: "orange",
                            fontWeight: "600",
                            fontSize: "13px",
                            background: "#fff"
                        }}>
                            {currentPage}
                        </div>

                        {/* NEXT */}
                        <button
                            disabled={!nextPage}
                            onClick={() => onPageChange(currentPage + 1)}
                            style={{
                                border: "none",
                                background: "transparent",
                                cursor: !nextPage ? "not-allowed" : "pointer",
                                padding: "4px 8px",
                                color: "#666"
                            }}
                        >
                            ›
                        </button>

                    </div>
                </div>
            </div>

            {/* Load More footer */}
            {!loading && visibleRows.length > 0 && (
                <div className="d-flex align-items-center justify-content-between mt-2" style={{ padding: "0 2px" }}>
                    <span style={{ fontSize: 12, color: "#aaa" }}>Showing {data.length} categories</span>
                    {loadingMore && (
                        <span style={{ fontSize: 12, color: "#888", display: "flex", alignItems: "center", gap: 6 }}>
                            <div className="spinner-border spinner-border-sm" style={{ width: 12, height: 12, borderWidth: 2 }} /> Loading more…
                        </span>
                    )}
                    {!hasMore && <span style={{ fontSize: 12, color: "#ccc" }}>All loaded</span>}
                </div>
            )}
        </div>
    );
};

const thStyle = {
    padding: "7px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "#555",
    borderBottom: "1px solid #EBEAF2",
    background: "#f8f9fa",
    whiteSpace: "nowrap",
};

const tdStyle = {
    padding: "6px 12px",
    fontSize: 13,
    verticalAlign: "middle",
    borderBottom: "1px solid #f5f5f5",
};

const toggleBtnStyle = {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "1px 3px",
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
};

const moreBtn = {
    background: "none",
    border: "1px solid #e8e8e8",
    borderRadius: 6,
    cursor: "pointer",
    padding: "4px 7px",
    display: "flex",
    alignItems: "center",
    gap: 3,
};

const dotStyle = {
    width: 3,
    height: 3,
    borderRadius: "50%",
    background: "#666",
    display: "inline-block",
};

const dropdownStyle = {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "white",
    border: "1px solid #e8e8e8",
    borderRadius: 8,
    boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
    zIndex: 100,
    minWidth: 130,
    overflow: "hidden",
};

const menuItemStyle = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "8px 14px",
    background: "white",
    border: "none",
    cursor: "pointer",
    fontSize: 13,
    color: "#333",
    textAlign: "left",
    transition: "background 0.15s",
};

export default ProductCategoryTable;
